import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { ChevronDown, ChevronRight, Trash2, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Two-panel grade picker.
 *
 * Left: source schedules (accordion). Expand → shows the schedule's
 * grades with a checkbox each.
 * Right: basket (the grades the user has chosen). Also shows the source
 * schedule as a badge and lets the user remove picks.
 *
 * Props
 * -----
 *   open, onOpenChange - dialog state
 *   mode = 'compose' | 'append'
 *   targetScheduleId - required when mode === 'append'; excluded from
 *                      the source list and used to check collisions.
 *   onCompleted - callback(created_schedule_id_or_none) fired on success
 */
export function GradePickerDialog({ open, onOpenChange, mode, targetScheduleId, onCompleted }) {
  const [allSchedules, setAllSchedules] = useState([]);
  const [allGrades, setAllGrades] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [picks, setPicks] = useState([]); // list of grade_ids in order
  const [saving, setSaving] = useState(false);
  const [collisionWarning, setCollisionWarning] = useState(null);
  // 'empty' = start from scratch (backend POST /content/schedules)
  // 'clone' = pick grades from existing schedules (POST /schedules/compose)
  const [startMode, setStartMode] = useState('empty');
  const [meta, setMeta] = useState({
    schedule_type: '',
    schedule_description: '',
    special_event: false,
    two_round_events: false,
    skate_mf_together: true,
    judge_mf_together: true,
  });

  useEffect(() => {
    if (!open) return;
    setPicks([]);
    setExpanded(new Set());
    setCollisionWarning(null);
    setStartMode(mode === 'append' ? 'clone' : 'empty');
    setMeta({
      schedule_type: '',
      schedule_description: '',
      special_event: false,
      two_round_events: false,
      skate_mf_together: true,
      judge_mf_together: true,
    });
    axios.get(`${API}/content/schedules`, { withCredentials: true })
      .then(r => setAllSchedules(r.data || []))
      .catch(() => {});
    axios.get(`${API}/content/grades`, { withCredentials: true })
      .then(r => setAllGrades(r.data || []))
      .catch(() => {});
    axios.get(`${API}/content/disciplines`, { withCredentials: true })
      .then(r => setDisciplines(r.data || []))
      .catch(() => {});
  }, [open]);

  const disciplineNameById = useMemo(
    () => Object.fromEntries(disciplines.map(d => [d.discipline_id, d.discipline || d.name])),
    [disciplines]
  );

  const gradeLabel = (g) => {
    const disc = disciplineNameById[g.discipline_id];
    return disc ? `${g.name} ${disc}` : g.name;
  };

  // Collision key includes discipline so "Whetū A Figures" and
  // "Whetū A Loops" are treated as distinct grades.
  const collisionKey = (g) => `${(g.name || '').toLowerCase()}|${g.discipline_id || ''}`;

  // For append mode, remember the existing grade collision keys in the
  // target schedule so we can flag same-name adds.
  const existingKeys = useMemo(() => {
    if (mode !== 'append' || !targetScheduleId) return new Set();
    return new Set(
      allGrades
        .filter(g => g.schedule_id === targetScheduleId)
        .map(collisionKey)
    );
  }, [mode, targetScheduleId, allGrades]);

  const gradesByScheduleId = useMemo(() => {
    const m = {};
    for (const g of allGrades) {
      const sid = g.schedule_id;
      if (!sid) continue;
      if (mode === 'append' && sid === targetScheduleId) continue; // don't self-pick
      if (!m[sid]) m[sid] = [];
      m[sid].push(g);
    }
    for (const sid of Object.keys(m)) {
      m[sid].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    }
    return m;
  }, [allGrades, mode, targetScheduleId]);

  const gradeById = useMemo(
    () => Object.fromEntries(allGrades.map(g => [g.grade_id, g])),
    [allGrades]
  );
  const schedById = useMemo(
    () => Object.fromEntries(allSchedules.map(s => [s.schedule_id, s])),
    [allSchedules]
  );

  const listedSchedules = useMemo(() => allSchedules
    .filter(s => !s.is_draft)
    .filter(s => mode !== 'append' || s.schedule_id !== targetScheduleId)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
  , [allSchedules, mode, targetScheduleId]);

  const pickedSet = useMemo(() => new Set(picks), [picks]);
  const keysInBasket = useMemo(() => new Set(
    picks.map(gid => {
      const g = gradeById[gid];
      return g ? collisionKey(g) : '';
    }).filter(Boolean)
  ), [picks, gradeById]);

  const toggleExpanded = (sid) => {
    const n = new Set(expanded);
    if (n.has(sid)) n.delete(sid); else n.add(sid);
    setExpanded(n);
  };

  const attemptAdd = (grade) => {
    if (pickedSet.has(grade.grade_id)) {
      // toggle off
      setPicks(picks.filter(g => g !== grade.grade_id));
      return;
    }
    const key = collisionKey(grade);
    const clash = existingKeys.has(key) || keysInBasket.has(key);
    if (clash) {
      // Show the collision-warning modal per the PRD; add on confirm.
      setCollisionWarning({ grade });
      return;
    }
    setPicks([...picks, grade.grade_id]);
  };

  const confirmCollisionAdd = () => {
    if (collisionWarning?.grade) {
      setPicks([...picks, collisionWarning.grade.grade_id]);
    }
    setCollisionWarning(null);
  };

  const removePick = (grade_id) => setPicks(picks.filter(g => g !== grade_id));

  const selectAllFromSchedule = (schedule_id, e) => {
    e.stopPropagation();  // don't toggle the accordion
    const scheduleGrades = gradesByScheduleId[schedule_id] || [];
    const allPicked = scheduleGrades.length > 0 && scheduleGrades.every(g => pickedSet.has(g.grade_id));
    if (allPicked) {
      // Deselect: remove every grade from this schedule that's currently in the basket
      const toRemove = new Set(scheduleGrades.map(g => g.grade_id));
      setPicks(picks.filter(gid => !toRemove.has(gid)));
      return;
    }
    // Select-all path: append every grade that isn't already picked.
    // Collision warning is skipped for bulk-select (would be spammy);
    // the backend still enforces the "(source schedule)" rename.
    const toAdd = scheduleGrades
      .map(g => g.grade_id)
      .filter(gid => !pickedSet.has(gid));
    if (toAdd.length === 0) return;
    setPicks([...picks, ...toAdd]);
  };

  const submit = async () => {
    if (mode === 'compose' && startMode === 'empty') {
      if (!meta.schedule_type.trim() || !meta.schedule_description.trim()) {
        toast.error('Schedule name and description are required');
        return;
      }
      setSaving(true);
      try {
        const r = await axios.post(`${API}/content/schedules`, meta, { withCredentials: true });
        toast.success('Schedule created');
        onOpenChange(false);
        onCompleted?.(r.data?.schedule_id);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (picks.length === 0) {
      toast.error('Pick at least one grade');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'compose') {
        if (!meta.schedule_type.trim() || !meta.schedule_description.trim()) {
          toast.error('Schedule name and description are required');
          setSaving(false);
          return;
        }
        const r = await axios.post(`${API}/schedules/compose`, {
          ...meta,
          grade_ids: picks,
        }, { withCredentials: true });
        toast.success(`Composed with ${r.data.grades_added} grade(s).`);
        onOpenChange(false);
        onCompleted?.(r.data.schedule_id);
      } else {
        const r = await axios.post(`${API}/schedules/${targetScheduleId}/add-grades`, {
          grade_ids: picks,
        }, { withCredentials: true });
        toast.success(`Added ${r.data.grades_added} grade(s).`);
        onOpenChange(false);
        onCompleted?.(targetScheduleId);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl" data-testid="grade-picker-dialog">
          <DialogHeader>
            <DialogTitle>
              {mode === 'compose' ? 'Add Schedule' : 'Add grades from other schedules'}
            </DialogTitle>
          </DialogHeader>

          {mode === 'compose' && (
            <div className="grid grid-cols-2 gap-4 pb-3 border-b border-stone-200">
              <div>
                <Label>Schedule name *</Label>
                <Input
                  value={meta.schedule_type}
                  onChange={(e) => setMeta({ ...meta, schedule_type: e.target.value })}
                  placeholder="e.g. Auckland Regional"
                  data-testid="compose-name-input"
                />
              </div>
              <div>
                <Label>Description *</Label>
                <Input
                  value={meta.schedule_description}
                  onChange={(e) => setMeta({ ...meta, schedule_description: e.target.value })}
                  placeholder="Describe the schedule e.g. Official SkateNZ grades for New Zealand Competitions"
                  data-testid="compose-desc-input"
                />
              </div>
              <div className="col-span-2 flex flex-wrap gap-4">
                <label className="flex items-center gap-2">
                  <Checkbox checked={meta.two_round_events}
                            onCheckedChange={(v) => setMeta({ ...meta, two_round_events: !!v })} />
                  <span className="text-sm">Supports two-round events</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox checked={meta.special_event}
                            onCheckedChange={(v) => setMeta({ ...meta, special_event: !!v })} />
                  <span className="text-sm">Special event</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox checked={meta.skate_mf_together}
                            onCheckedChange={(v) => setMeta({ ...meta, skate_mf_together: !!v })} />
                  <span className="text-sm">Skate M/F together</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox checked={meta.judge_mf_together}
                            onCheckedChange={(v) => setMeta({ ...meta, judge_mf_together: !!v })} />
                  <span className="text-sm">Judge M/F together</span>
                </label>
              </div>
              <div className="col-span-2 pt-2">
                <Label className="text-sm font-semibold text-[#2D4A3E] mb-2 block">
                  How do you want to build this schedule?
                </Label>
                <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="compose-start-mode"
                      value="empty"
                      checked={startMode === 'empty'}
                      onChange={() => setStartMode('empty')}
                      className="mt-1"
                      data-testid="compose-mode-empty"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Start empty</span>
                      <span className="block text-xs text-muted-foreground">Add your first grade once the schedule is created.</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="compose-start-mode"
                      value="clone"
                      checked={startMode === 'clone'}
                      onChange={() => setStartMode('clone')}
                      className="mt-1"
                      data-testid="compose-mode-clone"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Clone grades from existing schedules</span>
                      <span className="block text-xs text-muted-foreground">Pick grades from any schedule below to seed this one.</span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {(mode === 'append' || startMode === 'clone') && (
          <div className="grid grid-cols-2 gap-4 mt-3" style={{ minHeight: 400 }}>
            {/* Left: source schedules */}
            <div className="border rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-stone-200 bg-stone-50 font-semibold text-sm">
                Source schedules
              </div>
              <div className="max-h-[420px] overflow-y-auto">
                {listedSchedules.map(s => {
                  const isOpen = expanded.has(s.schedule_id);
                  const grades = gradesByScheduleId[s.schedule_id] || [];
                  const pickedCount = grades.filter(g => pickedSet.has(g.grade_id)).length;
                  const allPicked = grades.length > 0 && pickedCount === grades.length;
                  return (
                    <div key={s.schedule_id} className="border-b border-stone-100">
                      <button
                        className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-stone-50"
                        onClick={() => toggleExpanded(s.schedule_id)}
                        data-testid={`source-schedule-${s.schedule_id}`}
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <span className="font-medium">{s.schedule_type}</span>
                        <span className="text-xs text-muted-foreground ml-auto flex items-center gap-3">
                          <span>
                            {pickedCount > 0 && <span className="text-emerald-700 font-medium">{pickedCount}/</span>}
                            {grades.length} grade{grades.length === 1 ? '' : 's'}
                          </span>
                          {grades.length > 0 && (
                            <span
                              role="button"
                              className="px-2 py-0.5 rounded border border-stone-300 text-emerald-700 hover:bg-emerald-50 whitespace-nowrap"
                              onClick={(e) => selectAllFromSchedule(s.schedule_id, e)}
                              data-testid={`select-all-${s.schedule_id}`}
                            >
                              {allPicked ? 'Deselect all' : 'Select all'}
                            </span>
                          )}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="pl-8 pr-3 pb-2 space-y-1">
                          {grades.map(g => (
                            <label
                              key={g.grade_id}
                              className={`flex items-center gap-2 py-1 cursor-pointer text-sm hover:bg-emerald-50 rounded ${pickedSet.has(g.grade_id) ? 'text-emerald-700 font-medium' : ''}`}
                            >
                              <Checkbox
                                checked={pickedSet.has(g.grade_id)}
                                onCheckedChange={() => attemptAdd(g)}
                                data-testid={`pick-grade-${g.grade_id}`}
                              />
                              <span className="truncate">{gradeLabel(g)}</span>
                              {g.classification && (
                                <span className="text-xs text-stone-400 ml-auto">{g.classification}</span>
                              )}
                            </label>
                          ))}
                          {grades.length === 0 && (
                            <div className="text-xs text-muted-foreground py-1">No grades in this schedule.</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: basket */}
            <div className="border rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-stone-200 bg-stone-50 font-semibold text-sm flex items-center justify-between">
                <span>Your picks ({picks.length})</span>
                {picks.length > 0 && (
                  <button className="text-xs text-muted-foreground hover:text-red-600" onClick={() => setPicks([])}>
                    Clear all
                  </button>
                )}
              </div>
              <div className="max-h-[420px] overflow-y-auto">
                {picks.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    Pick grades from the left panel to build your schedule.
                  </div>
                ) : picks.map((gid, idx) => {
                  const g = gradeById[gid];
                  if (!g) return null;
                  const src = schedById[g.schedule_id];
                  return (
                    <div key={gid} className="flex items-center gap-2 px-3 py-2 border-b border-stone-100 text-sm" data-testid={`basket-item-${gid}`}>
                      <span className="text-xs text-stone-400 w-6">{idx + 1}.</span>
                      <span className="flex-1 truncate">{gradeLabel(g)}</span>
                      <span className="text-xs text-stone-500 truncate max-w-[120px]" title={src?.schedule_type}>
                        {src?.schedule_type || '?'}
                      </span>
                      <button className="text-stone-400 hover:text-red-600" onClick={() => removePick(gid)} data-testid={`remove-pick-${gid}`}>
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          )}

          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              className="btn-primary"
              onClick={submit}
              disabled={saving || (startMode === 'clone' && picks.length === 0)}
              data-testid="grade-picker-submit-btn"
            >
              {saving
                ? 'Saving…'
                : mode === 'compose'
                  ? (startMode === 'empty'
                      ? 'Create schedule'
                      : `Create schedule with ${picks.length} grade${picks.length === 1 ? '' : 's'}`)
                  : `Add ${picks.length} grade${picks.length === 1 ? '' : 's'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Collision warning modal — user-supplied copy */}
      <Dialog open={!!collisionWarning} onOpenChange={(v) => { if (!v) setCollisionWarning(null); }}>
        <DialogContent className="max-w-md" data-testid="collision-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Grade name already exists in your schedule
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-stone-700">
            You already have a grade with this name in the schedule that you are building.<br /><br />
            The grade will be added with the name of the existing schedule trailing in brackets.
            It&apos;s recommended that you give it its own unique name once your schedule is ready to edit.
          </div>
          <div className="flex justify-end pt-3">
            <Button className="btn-primary" onClick={confirmCollisionAdd} data-testid="collision-ok-btn">
              Okay
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
